import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { apiService } from '../services/apiService'

const SuperAdminAuthContext = createContext()

const IDLE_TIMEOUT_MS = 15 * 60 * 1000 // 15 minutes
const VISIBILITY_GRACE_MS = 30 * 1000 // 30 seconds grace when tab hidden

export const useSuperAdminAuth = () => {
  const context = useContext(SuperAdminAuthContext)
  if (!context) {
    throw new Error('useSuperAdminAuth must be used within a SuperAdminAuthProvider')
  }
  return context
}

export const SuperAdminAuthProvider = ({ children }) => {
  const [superAdmin, setSuperAdmin] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const idleTimerRef = useRef(null)
  const visibilityTimerRef = useRef(null)
  const isAuthenticatedRef = useRef(false)

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated
  }, [isAuthenticated])

  useEffect(() => {
    checkAuthStatus()
  }, [])

  // Idle timeout & beforeunload setup
  useEffect(() => {
    if (!isAuthenticated) return

    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      idleTimerRef.current = setTimeout(() => {
        console.warn('Super admin session timed out due to inactivity')
        logout()
      }, IDLE_TIMEOUT_MS)
    }

    const handleActivity = () => resetIdleTimer()
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    activityEvents.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }))
    resetIdleTimer()

    const handleBeforeUnload = () => {
      if (isAuthenticatedRef.current) {
        apiService.beaconLogout(false, true)
        sessionStorage.removeItem('superAdminToken')
        sessionStorage.removeItem('superAdminData')
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)

    const handleVisibility = () => {
      if (document.hidden) {
        visibilityTimerRef.current = setTimeout(() => {
          console.warn('Super admin session ended — tab hidden too long')
          logout()
        }, VISIBILITY_GRACE_MS)
      } else {
        if (visibilityTimerRef.current) clearTimeout(visibilityTimerRef.current)
        resetIdleTimer()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      activityEvents.forEach(evt => window.removeEventListener(evt, handleActivity))
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibility)
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      if (visibilityTimerRef.current) clearTimeout(visibilityTimerRef.current)
    }
  }, [isAuthenticated])

  const checkAuthStatus = async () => {
    try {
      const token = sessionStorage.getItem('superAdminToken')
      const superAdminData = sessionStorage.getItem('superAdminData')
      
      if (token && superAdminData) {
        setSuperAdmin(JSON.parse(superAdminData))
        setIsAuthenticated(true)
      }
    } catch (error) {
      console.error('Super admin auth check failed:', error)
      logout()
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (loginData) => {
    try {
      setIsLoading(true)
      
      // Super admin uses dedicated SUPER-ADMIN-LOGIN endpoint
      const response = await apiService.superAdminLogin(loginData)

      // API returns 'admin' object, not 'user'
      const adminData = response.admin || response.user
      
      if (response.success && adminData && adminData.role === 'super_admin') {
        sessionStorage.setItem('superAdminToken', response.token)
        sessionStorage.setItem('superAdminData', JSON.stringify(adminData))
        setSuperAdmin(adminData)
        setIsAuthenticated(true)
        return { success: true }
      } else {
        return { success: false, error: response.message || 'Super admin access denied' }
      }
    } catch (error) {
      console.error('Super admin login failed:', error)
      return { success: false, error: 'Login failed. Please try again.' }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    apiService.serverLogout(false, true).catch(() => {})
    sessionStorage.removeItem('superAdminToken')
    sessionStorage.removeItem('superAdminData')
    setSuperAdmin(null)
    setIsAuthenticated(false)
  }

  const value = {
    superAdmin,
    isAuthenticated,
    isLoading,
    login,
    logout
  }

  return (
    <SuperAdminAuthContext.Provider value={value}>
      {children}
    </SuperAdminAuthContext.Provider>
  )
}
